import { useCallback, useMemo, useState } from 'react';
import type { DirectedCastEntry } from 'fym-shared/src/music24/directedVideoApi';
import { useGetAccountsQuery } from '../../../redux/api/fymAPI';
import { useUserToken } from '../../../redux/slices/appInfo/hooks';
import { directedActionError, directedErrorCode } from '../directedErrors';
import { useAddDirectedCastFromArtistPictureMutation, type DirectedVideoArgs } from '../directedVideoApi';
import type { CastUploadError } from './useCastUpload';

/** The artist picture that the cast step offers. */
export interface ArtistPictureOffer {
  /** The picture as the app draws artist pictures (`avatarProcessedUrl`, an imgproxy square). */
  url: string;
  /** The artist name, for the alt text. */
  name: string;
}

/**
 * The artist picture of the account as one choice of the cast step (owner request 2026-10-19:
 * "allow to pick your user avatar as one of the characters"). A Music24 user has no own avatar: the
 * picture that the app shows for the user is the artist picture of the active artist. So the choice
 * shows only when this artist has a picture (`avatarFile`), and not when the cast already holds it
 * (`fromArtistPicture`). One tap copies it into the cast as "me" (`POST .../cast/artist-picture`).
 * It needs no consent tick: the choice of the own picture is the confirmation.
 */
export function useArtistPictureChoice(args: DirectedVideoArgs, cast: readonly DirectedCastEntry[]) {
  const userToken = useUserToken();
  const { account } = useGetAccountsQuery(undefined, {
    skip: !userToken,
    selectFromResult: ({ data }) => ({
      account: data?.accounts.find((one) => one.id === args.accountId),
    }),
  });
  const [addArtistPicture, adding] = useAddDirectedCastFromArtistPictureMutation();
  const [error, setError] = useState<CastUploadError | null>(null);

  const isInCast = cast.some((entry) => entry.fromArtistPicture === true);
  const offer = useMemo((): ArtistPictureOffer | null => {
    if (account === undefined || account.isDemo === true || account.avatarFile === null) {
      return null;
    }
    const url = account.avatarProcessedUrl;
    if (url === null || url === '') {
      return null;
    }
    return { url, name: account.name ?? '' };
  }, [account]);

  const add = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      await addArtistPicture(args).unwrap();
    } catch (caught) {
      console.error('Directed video: the artist picture was not added to the cast', caught);
      const mapped = directedActionError(caught);
      // A 404 (no picture any more) and every other plain failure say "upload a photo instead".
      setError(mapped === 'failed' && directedErrorCode(caught) === null ? 'artistPicture' : mapped);
    }
  }, [addArtistPicture, args]);

  const clearError = useCallback(() => setError(null), []);

  return {
    offer: isInCast ? null : offer,
    add,
    isAdding: adding.isLoading,
    error,
    clearError,
  };
}
